import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Form, Input, Typography } from 'antd'
import useMutation from 'hooks/useMutation'
import { CurrentUser } from 'models/User'
import Api from 'services/api'
import {
  apiDomain,
  clearIdTokens,
  getToken,
  refreshToken
} from 'services/authAxios'
import useSWR from 'swr'
import { LocalAuthStorageKey } from 'types'

import Button from 'components/atoms/Button'

import OverCountVerifyModal from './over-count-verify-otp-modal'

export default function SessionExpiredOtpVerify() {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const email = searchParams.get('email') || ''
  const [isResendCode, setIsResendCode] = useState<boolean>(true)
  const [resendEmailCount, setResendEmailCount] = useState<number>(0)
  const [retryCount, setRetryCount] = useState<number>(0)
  const [showOverCountModal, setShowOverCountModal] = useState<boolean>(false)
  const [isInputDisabled, setIsInputDisabled] = useState<boolean>(false)
  const maxRetryAttempts = 5
  const resendCountDownRef = useRef<NodeJS.Timeout | null>(null)

  const resendTimeout =
    apiDomain?.includes('dev.digi-tori360.com') ||
    apiDomain?.includes('rc.digi-tori360.com')
      ? 5 * 60
      : 15 * 60

  const [resendCountDown, setResendCountDown] = useState<number>(resendTimeout)
  const displayMinutes = Math.floor(resendCountDown / 60)
  const displaySeconds = resendCountDown % 60

  const navigate = useNavigate()

  const { trigger: triggerResendVerify, isMutating: isResendingVerify } =
    useMutation('/v1/auth/email/verify')

  const { trigger: triggerVerifyOTPCode, isMutating: isVerifyingOTPCode } =
    useMutation('v1/auth/email/verify-code')

  const { mutate } = useSWR<CurrentUser | null>('v1/users/me', Api.get, {
    revalidateOnMount: false
  })

  /**
   * Handle resend code
   * @returns void
   */
  const handleResendCode = async () => {
    await refreshToken()
    if (!isResendingVerify && email) {
      setResendCountDown(resendTimeout)
      setResendEmailCount((prev) => prev + 1)
      setIsResendCode(true)

      triggerResendVerify(
        {
          data: {
            email,
            // use acessToken for get user info because refresh token can not get user info
            refreshToken: getToken().idToken
          }
        },
        {
          onSuccess: () => {
            setIsInputDisabled(false)
          }
        }
      )
    }
  }

  /**
   * Handle verify OTP code
   * @param values
   * @returns void
   */
  const handleVerifyOTPCode = (values: any) => {
    if (retryCount >= 4) {
      setIsInputDisabled(true)
    }
    if (retryCount >= maxRetryAttempts) {
      // Redirect to sign-in if max retries reached
      localStorage.removeItem(LocalAuthStorageKey.PREVIOUS_SESSION_EXPIRED_PATH)
      localStorage.removeItem('last_activity')
      clearIdTokens()
      navigate('/signin')
      return
    }

    triggerVerifyOTPCode(
      {
        data: {
          email,
          code: values.code.toString()
        }
      },
      {
        onSuccess: () => {
          mutate()

          const previousPath = localStorage.getItem(
            LocalAuthStorageKey.PREVIOUS_SESSION_EXPIRED_PATH
          )
          // Clear the stored path to prevent future issues
          localStorage.removeItem(
            LocalAuthStorageKey.PREVIOUS_SESSION_EXPIRED_PATH
          )

          // Use window.location.replace for a clean redirect
          if (previousPath) {
            window.location.replace(previousPath)
          } else {
            window.location.replace('/')
          }
          localStorage.setItem('last_activity', Date.now().toString())
        },
        onError: () => {
          const newRetryCount = retryCount + 1
          setRetryCount(newRetryCount)

          if (newRetryCount === 5) {
            setShowOverCountModal(true)
          } else if (newRetryCount >= maxRetryAttempts) {
            // Show final modal before redirecting on 10th attempt
            setShowOverCountModal(true)
          }
        }
      }
    )
  }

  /**
   * effect count down time can resend code
   * @returns void
   */
  useEffect(() => {
    if (isResendCode) {
      resendCountDownRef.current = setInterval(() => {
        setResendCountDown((prev) => {
          if (prev <= 1) {
            clearInterval(resendCountDownRef.current as NodeJS.Timeout)
            setIsResendCode(false)
            setRetryCount(0)
            return 0
          }
          return prev - 1
        })
      }, 1000)
    }

    return () => {
      if (resendCountDownRef.current) {
        clearInterval(resendCountDownRef.current)
      }
    }
  }, [isResendCode])

  return (
    <>
      <OverCountVerifyModal
        open={showOverCountModal}
        onCancel={() => setShowOverCountModal(false)}
        setRetryCount={setRetryCount}
        isMaxAttempts={retryCount >= maxRetryAttempts && resendEmailCount >= 1}
        handleResendCode={handleResendCode}
      />

      <div className="flex flex-col items-center justify-center min-h-screen p-4 pb-[78px]">
        <div className="w-full max-w-md p-8 bg-white rounded-2xl shadow-lg">
          <div className="text-center mb-8">
            <Typography className="!mb-2 !text-xl text-[#3163B7] font-bold">
              {t('sessionExpired.otpVerify.title')}
            </Typography>
            <div className="h-[1px] w-[72px] bg-[#3163B7] mx-auto mb-[30px]" />
            <Typography.Text
              type="secondary"
              className="text-xs font-[400] !text-[#021120]"
            >
              {t('sessionExpired.otpVerify.description')}
            </Typography.Text>
          </div>

          <Form layout="vertical" onFinish={handleVerifyOTPCode}>
            <Form.Item
              name="code"
              rules={[
                { required: true, message: '入力してください' },
                {
                  pattern: /^\d{6}$/,
                  message: '6桁の数字を入力してください'
                }
              ]}
              className="!mb-1"
            >
              <Input
                size="large"
                placeholder="入力してください"
                className="rounded-lg w-full"
                maxLength={6}
                disabled={isInputDisabled}
                onKeyPress={(e: React.KeyboardEvent<HTMLInputElement>) => {
                  // Only allow numbers
                  if (!/[0-9]/.test(e.key)) {
                    e.preventDefault()
                  }
                }}
              />
            </Form.Item>

            {retryCount > 0 && (
              <div className="text-right text-sm text-red-500 mb-2">
                {t('sessionExpired.otpVerify.retryCount').replace(
                  '{count}',
                  (maxRetryAttempts - retryCount).toString()
                )}
              </div>
            )}

            <div className="flex justify-end items-center mb-4">
              <div
                className={`text-xs font-[400] ${
                  isResendCode || isResendingVerify
                    ? 'text-[#a6aaaf] cursor-not-allowed'
                    : 'text-[#3163B7] cursor-pointer hover:underline'
                }`}
                onClick={
                  !isResendCode && !isResendingVerify
                    ? handleResendCode
                    : undefined
                }
              >
                {isResendingVerify ? (
                  <span>送信中...</span>
                ) : (
                  <>
                    {t('sessionExpired.otpVerify.resendCode')}
                    {isResendCode && resendCountDown > 0 && (
                      <span className="text-sm text-[#a6aaaf] ml-1">
                        ({displayMinutes}:
                        {displaySeconds < 10
                          ? `0${displaySeconds}`
                          : displaySeconds}
                        )
                      </span>
                    )}
                  </>
                )}
              </div>
            </div>

            <div className="flex justify-center items-center gap-x-[10px]">
              <Button
                sizing="w-full h-[60px]"
                className="cancel-button"
                onClick={() => navigate('/session-expired-verify-email')}
              >
                {t('sessionExpired.otpVerify.back')}
              </Button>

              <Button
                type="primary"
                sizing="w-full h-[60px]"
                htmlType="submit"
                loading={isVerifyingOTPCode}
                disabled={isInputDisabled}
              >
                {t('sessionExpired.otpVerify.verify')}
              </Button>
            </div>
          </Form>
        </div>
      </div>
    </>
  )
}
